import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  Col,
  Dropdown,
  Menu,
  Modal,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  BankOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  EyeOutlined,
  GlobalOutlined,
  PlusOutlined,
  StopOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  ApartmentOutlined,
  EllipsisOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import { API_URL, TOKEN } from "../../variables/constants";
import AddMembres from "./AddMembres";
import ExportableFilterableTable from "../../components/tables/ExportableFilterableTable";

const { confirm } = Modal;
const { Title, Text } = Typography;

const TYPE_CONFIG = {
  ASSURANCE: { label: "Assurance", color: "blue", icon: <SafetyCertificateOutlined /> },
  PARTENAIRE: { label: "Partenaire", color: "green", icon: <GlobalOutlined /> },
  CONCESSIONNAIRE: { label: "Concessionnaire", color: "orange", icon: <ApartmentOutlined /> },
};

const GRADIENT = {
  total:          "linear-gradient(135deg, #242166 0%, #1890ff 100%)",
  assurance:      "linear-gradient(135deg, #1890ff 0%, #36cfc9 100%)",
  partenaire:     "linear-gradient(135deg, #52c41a 0%, #95de64 100%)",
  concessionnaire:"linear-gradient(135deg, #fa8c16 0%, #ffd666 100%)",
};

/* ── Petit composant de stat ──────────────────────────────────── */
const StatCard = ({ gradient, icon, label, value, loading }) => (
  <Card
    style={{ borderRadius: 12, background: gradient, border: "none" }}
    styles={{ body: { padding: "20px 24px" } }}
  >
    <Space align="center" size={16}>
      <Avatar
        size={48}
        icon={icon}
        style={{ background: "rgba(255,255,255,0.2)", flexShrink: 0 }}
      />
      <div>
        <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 13 }}>{label}</Text>
        <div style={{ color: "#fff", fontSize: 28, fontWeight: 700, lineHeight: 1.2 }}>
          {loading ? "—" : value}
        </div>
      </div>
    </Space>
  </Card>
);

/* ── Page principale ──────────────────────────────────────────── */
const AllMembres = () => {
  const [assurances, setAssurances]       = useState([]);
  const [partenaires, setPartenaires]     = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showModal, setShowModal]         = useState(false);
  const [editMode, setEditMode]           = useState(false);
  const [selectedMembre, setSelectedMembre] = useState(null);
  const navigate = useNavigate();

  /* ── Chargement ───────────────────────────────────────────── */
  useEffect(() => {
    setLoading(true);
    const headers = { Authorization: `Bearer ${TOKEN}` };

    Promise.all([
      axios.get(`${API_URL}/api/organisme/assurance`, { headers }),
      axios.get(`${API_URL}/api/organisme/partenaire`, { headers }),
    ])
      .then(([resA, resP]) => {
        setAssurances(resA.data);
        setPartenaires(resP.data);
      })
      .catch(() => toast.error("Erreur lors du chargement des membres"))
      .finally(() => setLoading(false));
  }, []);

  /* ── Données fusionnées ───────────────────────────────────── */
  const dataSource = useMemo(
    () =>
      [...assurances, ...partenaires].map((item, idx) => ({
        key: item.organismeId ?? idx,
        ...item,
      })),
    [assurances, partenaires]
  );

  const stats = useMemo(() => ({
    total:           dataSource.length,
    assurance:       assurances.length,
    partenaire:      partenaires.filter(p => p.typeOrganisme === "PARTENAIRE").length,
    concessionnaire: partenaires.filter(p => p.typeOrganisme === "CONCESSIONNAIRE").length,
  }), [dataSource, assurances, partenaires]);

  /* ── Toggle statut Pool ───────────────────────────────────── */
  const handleTogglePool = (record) => {
    const retire = record.active;
    confirm({
      title: retire ? "Retirer du Pool ?" : "Réintégrer au Pool ?",
      icon: (
        <ExclamationCircleFilled
          style={{ color: retire ? "#faad14" : "#52c41a" }}
        />
      ),
      content: retire
        ? `"${record.raisonSociale}" n'apparaîtra plus dans le carrousel de la page de connexion. Son portefeuille historique est conservé.`
        : `"${record.raisonSociale}" sera à nouveau affiché comme membre actif du Pool.`,
      okText: retire ? "Retirer du Pool" : "Réintégrer au Pool",
      okType: retire ? "default" : "primary",
      cancelText: "Annuler",
      onOk: () =>
        axios
          .get(`${API_URL}/api/organisme/${record.organismeId}/0`, {
            headers: { Authorization: `Bearer ${TOKEN}` },
          })
          .then((res) => {
            const updated = res.data;
            const updater = (list) =>
              list.map((item) =>
                item.organismeId === record.organismeId
                  ? { ...item, organismeId: updated.organismeId, active: updated.active }
                  : item
              );
            setAssurances((prev) => updater(prev));
            setPartenaires((prev) => updater(prev));
            toast.success(
              retire
                ? `${record.raisonSociale} retiré du Pool`
                : `${record.raisonSociale} réintégré au Pool`
            );
          })
          .catch(() => toast.error("Erreur lors de la mise à jour du statut")),
    });
  };

  /* ── Actions ──────────────────────────────────────────────── */
  const handleShowModal = (membre = null) => {
    setSelectedMembre(membre);
    setEditMode(!!membre);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedMembre(null);
  };

  const showDeleteConfirm = (organismeId) => {
    confirm({
      title: "Supprimer ce membre ?",
      icon: <ExclamationCircleFilled style={{ color: "#ff4d4f" }} />,
      content: "Cette action est irréversible et peut corrompre les données liées.",
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: () =>
        axios
          .delete(`${API_URL}/api/organisme/${organismeId}`, {
            headers: { Authorization: `Bearer ${TOKEN}` },
          })
          .then(() => {
            setAssurances(prev => prev.filter(i => i.organismeId !== organismeId));
            setPartenaires(prev => prev.filter(i => i.organismeId !== organismeId));
            toast.success("Membre supprimé avec succès");
          })
          .catch(() => toast.error("Erreur lors de la suppression")),
    });
  };

  /* ── Colonnes ─────────────────────────────────────────────── */
  const columns = [
    {
      title: "Membre",
      dataIndex: "raisonSociale",
      key: "raisonSociale",
      searchable: true,
      render: (text, record) => (
        <Space>
          <Avatar
            size={36}
            style={{
              background:
                record.typeOrganisme === "ASSURANCE"
                  ? "#1890ff"
                  : record.typeOrganisme === "PARTENAIRE"
                  ? "#52c41a"
                  : "#fa8c16",
              flexShrink: 0,
              fontWeight: 600,
            }}
          >
            {text?.charAt(0)?.toUpperCase() ?? "?"}
          </Avatar>
          <div>
            <div>
              <a
                href={`/membre/${record.code}`}
                style={{ fontWeight: 500, color: "#242166" }}
              >
                {text}
              </a>
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Code : {record.code}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "typeOrganisme",
      key: "typeOrganisme",
      width: 160,
      filterType: "select",
      filterPlaceholder: "Tous les types",
      filterOptions: [
        { label: "Assurance", value: "ASSURANCE" },
        { label: "Partenaire", value: "PARTENAIRE" },
        { label: "Concessionnaire", value: "CONCESSIONNAIRE" },
      ],
      render: (type) => {
        const cfg = TYPE_CONFIG[type];
        if (!cfg) return <Text type="secondary">—</Text>;
        return (
          <Tag color={cfg.color} icon={cfg.icon}>
            {cfg.label}
          </Tag>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      searchable: true,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: "Responsable",
      dataIndex: "responsable",
      key: "responsable",
      searchable: true,
      render: (v) =>
        v ? (
          <Space size={4}>
            <TeamOutlined style={{ color: "#8c8c8c" }} />
            {v}
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Création",
      dataIndex: "dateCreation",
      key: "dateCreation",
      width: 120,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: "Statut Pool",
      dataIndex: "active",
      key: "active",
      width: 130,
      render: (active) =>
        active ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            Membre actif
          </Tag>
        ) : (
          <Tag color="default" icon={<StopOutlined />}>
            Ex-membre
          </Tag>
        ),
    },
    {
      title: "",
      key: "actions",
      width: 48,
      hideInExport: true,
      render: (_, record) => {
        const menu = (
          <Menu
            items={[
              {
                key: "view",
                icon: <EyeOutlined />,
                label: "Voir les détails",
                onClick: () => navigate(`/membre/${record.code}`),
              },
              {
                key: "edit",
                icon: <EditOutlined />,
                label: "Modifier",
                onClick: () => handleShowModal(record),
              },
              { type: "divider" },
              {
                key: "toggle",
                icon: record.active
                  ? <StopOutlined style={{ color: "#faad14" }} />
                  : <CheckCircleOutlined style={{ color: "#52c41a" }} />,
                label: record.active ? "Retirer du Pool" : "Réintégrer au Pool",
                onClick: () => handleTogglePool(record),
              },
              { type: "divider" },
              {
                key: "delete",
                icon: <DeleteOutlined />,
                label: "Supprimer",
                danger: true,
                onClick: () => showDeleteConfirm(record.organismeId),
              },
            ]}
          />
        );
        return (
          <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight">
            <Tooltip title="Actions">
              <Button
                type="text"
                icon={<EllipsisOutlined />}
                style={{ color: "#8c8c8c" }}
              />
            </Tooltip>
          </Dropdown>
        );
      },
    },
  ];

  /* ── Rendu ────────────────────────────────────────────────── */
  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu={"MEMBRES"} />
        <div className="layout-page">
          <NavBar />

          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">

              {/* En-tête de page */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 24,
                  flexWrap: "wrap",
                  gap: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Avatar
                    size={44}
                    icon={<BankOutlined />}
                    style={{ background: "#242166" }}
                  />
                  <div>
                    <Title level={4} style={{ margin: 0, color: "#242166" }}>
                      Membres & Partenaires du Pool
                    </Title>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      Compagnies membres du GIE
                    </Text>
                  </div>
                </div>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => handleShowModal()}
                  style={{ background: "#242166", borderColor: "#242166" }}
                >
                  Nouveau membre
                </Button>
              </div>

              {/* Stat cards */}
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={12} sm={6}>
                  <StatCard
                    gradient={GRADIENT.total}
                    icon={<BankOutlined />}
                    label="Total membres"
                    value={stats.total}
                    loading={loading}
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <StatCard
                    gradient={GRADIENT.assurance}
                    icon={<SafetyCertificateOutlined />}
                    label="Assurances"
                    value={stats.assurance}
                    loading={loading}
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <StatCard
                    gradient={GRADIENT.partenaire}
                    icon={<GlobalOutlined />}
                    label="Partenaires"
                    value={stats.partenaire}
                    loading={loading}
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <StatCard
                    gradient={GRADIENT.concessionnaire}
                    icon={<ApartmentOutlined />}
                    label="Concessionnaires"
                    value={stats.concessionnaire}
                    loading={loading}
                  />
                </Col>
              </Row>

              {/* Tableau */}
              <Card
                style={{ borderRadius: 12 }}
                styles={{ body: { padding: 0 } }}
              >
                <ExportableFilterableTable
                  columns={columns}
                  dataSource={dataSource}
                  exportDataSource={dataSource}
                  loading={loading}
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                  exportFileName="membres-pool"
                  searchPlaceholder="Rechercher un membre, email, responsable..."
                />
              </Card>
            </div>

            <Footer />
          </div>
        </div>
      </div>

      {showModal && (
        <AddMembres
          show={showModal}
          handleClose={handleCloseModal}
          editMode={editMode}
          selectedMembre={selectedMembre}
        />
      )}
    </div>
  );
};

export default AllMembres;
